import { useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { Check, Headset, Moon } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useSupportTeams } from '@/features/support-tickets'
import type { FocusModeSetting } from '@/features/preferences'
import { cn } from '@/lib/utils'
import { FOCUS_CATEGORIES, untilLabel } from './focus'
import { useFocusMode } from './useFocusMode'

/**
 * Focus mode: pick what may still interrupt you, for how long, and (for on-call) a Support L3
 * watch that alerts the moment an L3 ticket matches your filter. Everything saves as you go, to
 * your account, so it follows you to every browser.
 */

const PRIORITIES = ['PX', 'P0', 'P1', 'P2', 'P3', 'P4']
const CLASSES = ['C1', 'C2', 'C3']
const SPRING = { type: 'spring', stiffness: 420, damping: 34 } as const

function tomorrowNine(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return d.toISOString()
}
const inHours = (h: number) => new Date(Date.now() + h * 3_600_000).toISOString()
const DURATIONS: Array<{ id: string; label: string; until: () => string | null }> = [
  { id: '1h', label: '1 hour', until: () => inHours(1) },
  { id: '4h', label: '4 hours', until: () => inHours(4) },
  { id: 'tomorrow', label: 'Until 9 AM tomorrow', until: tomorrowNine },
  { id: 'off', label: 'Until I turn it off', until: () => null },
]

export function FocusModeDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const reduce = useReducedMotion()
  const { focus, active, save } = useFocusMode()
  const { activeNames } = useSupportTeams()
  const [search, setSearch] = useState(focus.supportL3.search)
  const [duration, setDuration] = useState<string>(focus.until ? '' : 'off')

  const run = async (patch: Partial<FocusModeSetting>, message?: string) => {
    try {
      await save(patch)
      if (message) toast.success(message)
    } catch {
      toast.error('Could not save. Try again.')
    }
  }
  const watch = focus.supportL3
  const setWatch = (patch: Partial<FocusModeSetting['supportL3']>, message?: string) => run({ supportL3: { ...watch, ...patch } }, message)
  const toggleIn = (list: string[], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value])

  const watchSummary = [
    watch.teams.length ? watch.teams.join(', ') : 'any team',
    watch.priorities.length ? watch.priorities.join('/') : 'any priority',
    watch.clientClasses.length ? watch.clientClasses.join('/') : null,
    watch.assignedTo.length ? watch.assignedTo.map((a) => (a === 'me' ? 'assigned to me' : 'unassigned')).join(' or ') : null,
    watch.search ? `“${watch.search}”` : null,
  ].filter(Boolean).join(' · ')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Moon className="size-5 text-brand" /> Focus mode</DialogTitle>
          <DialogDescription>Choose what may still interrupt you. Everything else waits quietly in the bell.</DialogDescription>
        </DialogHeader>

        {/* Focus */}
        <section className={cn('rounded-2xl border p-4 transition-colors', active ? 'border-brand/40 bg-brand-soft' : 'border-border')}>
          <label className="flex cursor-pointer items-center gap-3">
            <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl transition-colors', active ? 'bg-brand text-text-inverse' : 'bg-surface-sunken text-text-secondary')}>
              <Moon className="size-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">Focus</span>
              <span className="block text-xs text-text-secondary">{active ? untilLabel(focus.until) : 'Off. All notifications come through.'}</span>
            </span>
            <Switch
              checked={active}
              onCheckedChange={(on) => void run({ enabled: on, until: on ? (DURATIONS.find((d) => d.id === duration)?.until() ?? null) : focus.until }, on ? 'Focus is on' : 'Focus is off')}
            />
          </label>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {DURATIONS.map((d) => {
              const selected = active ? (d.id === 'off' ? !focus.until : duration === d.id) : duration === d.id
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    setDuration(d.id)
                    void run({ enabled: true, until: d.until() }, `Focus on · ${untilLabel(d.until())}`)
                  }}
                  className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-colors', selected ? 'border-brand bg-brand text-text-inverse' : 'border-border bg-card text-text-secondary hover:border-brand/40 hover:text-foreground')}
                >
                  {d.label}
                </button>
              )
            })}
          </div>

          <p className="mt-4 mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-secondary">Let these through</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {FOCUS_CATEGORIES.map((c) => {
              const on = focus.allow.includes(c.id)
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => void run({ allow: toggleIn(focus.allow, c.id) })}
                  className={cn('flex items-start gap-2 rounded-xl border px-2.5 py-2 text-left transition-colors', on ? 'border-brand/50 bg-card' : 'border-transparent hover:bg-surface-sunken')}
                >
                  <span className={cn('mt-0.5 grid size-4 shrink-0 place-items-center rounded-[5px] border transition-colors', on ? 'border-brand bg-brand text-text-inverse' : 'border-border bg-card')}>
                    {on ? <Check className="size-3" strokeWidth={3} /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{c.label}</span>
                    <span className="block text-tiny text-text-muted">{c.detail}</span>
                  </span>
                </button>
              )
            })}
          </div>

          <label className="mt-3 flex cursor-pointer items-center gap-3 rounded-xl px-1 py-1">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">Quiet Slack & WhatsApp too</span>
              <span className="block text-xs text-text-secondary">Hold back their messages for anything muted above.</span>
            </span>
            <Switch checked={focus.external} onCheckedChange={(v) => void run({ external: v })} />
          </label>
        </section>

        {/* Support L3 watch */}
        <section className={cn('rounded-2xl border p-4 transition-colors', watch.enabled ? 'border-ios-green/50 bg-[color-mix(in_srgb,var(--color-ios-green)_7%,var(--color-card))]' : 'border-border')}>
          <label className="flex cursor-pointer items-center gap-3">
            <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl transition-colors', watch.enabled ? 'bg-ios-green text-white' : 'bg-surface-sunken text-text-secondary')}>
              <Headset className="size-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">Support L3 watch <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-tiny font-medium text-text-secondary">On-call</span></span>
              <span className="block text-xs text-text-secondary">Alert me when an L3 ticket matches these filters. Always comes through, even in Focus.</span>
            </span>
            <Switch checked={watch.enabled} onCheckedChange={(v) => void setWatch({ enabled: v }, v ? 'L3 watch is on' : 'L3 watch is off')} />
          </label>

          <motion.div
            initial={false}
            animate={{ height: watch.enabled ? 'auto' : 0, opacity: watch.enabled ? 1 : 0 }}
            transition={reduce ? { duration: 0 } : SPRING}
            className="overflow-hidden"
          >
            <div className="grid gap-3 pt-4">
              <Chips label="Teams" hint="none = any team" options={activeNames} selected={watch.teams} onToggle={(v) => void setWatch({ teams: toggleIn(watch.teams, v) })} />
              <Chips label="Priority" hint="none = any" options={PRIORITIES} selected={watch.priorities} onToggle={(v) => void setWatch({ priorities: toggleIn(watch.priorities, v) })} />
              <Chips label="Client class" hint="none = any" options={CLASSES} selected={watch.clientClasses} onToggle={(v) => void setWatch({ clientClasses: toggleIn(watch.clientClasses, v) })} />
              <Chips
                label="Assigned to"
                hint="none = anyone"
                options={['me', 'unassigned']}
                render={(v) => (v === 'me' ? 'Me' : 'Unassigned')}
                selected={watch.assignedTo}
                onToggle={(v) => void setWatch({ assignedTo: toggleIn(watch.assignedTo, v) })}
              />
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-secondary">Keyword</p>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onBlur={() => { if (search.trim() !== watch.search) void setWatch({ search: search.trim() }) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                  placeholder="Client, ticket number, words in the title…"
                  maxLength={200}
                />
              </div>
              <p className="rounded-xl bg-card px-3 py-2 text-xs text-text-secondary">
                <span className="font-semibold text-foreground">You’ll be alerted</span> on desktop, in the Dynamic Island and on Slack/WhatsApp (if on) when a ticket reaches L3 or changes to match: {watchSummary}.
              </p>
            </div>
          </motion.div>
        </section>
      </DialogContent>
    </Dialog>
  )
}

function Chips({ label, hint, options, selected, onToggle, render }: { label: string; hint: string; options: readonly string[]; selected: string[]; onToggle: (v: string) => void; render?: (v: string) => ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-secondary">{label} <span className="font-normal normal-case tracking-normal text-text-muted">· {hint}</span></p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = selected.includes(o)
          return (
            <button
              key={o}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(o)}
              className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-colors', on ? 'border-ios-green bg-ios-green text-white' : 'border-border bg-card text-text-secondary hover:border-ios-green/50 hover:text-foreground')}
            >
              {render ? render(o) : o}
            </button>
          )
        })}
      </div>
    </div>
  )
}
